import {
    Component,
    Input,
    Output,
    EventEmitter,
    ViewEncapsulation,
    ChangeDetectionStrategy,
    ContentChild,
    TemplateRef
} from '@angular/core';
import { scaleBand, scaleLinear } from 'd3-scale';

import { calculateViewDimensions, ViewDimensions } from '@swimlane/ngx-charts';
import { ColorHelper } from '@swimlane/ngx-charts';
import { BaseChartComponent } from '@swimlane/ngx-charts';

@Component({
    selector: 'ngx-charts-bar-horizontal-custom',
    template: `
      <ngx-charts-chart
        [view]="[width, height]"
        [showLegend]="legend"
        [legendOptions]="legendOptions"
        [activeEntries]="activeEntries"
        [animations]="animations"
        (legendLabelClick)="onClick($event)"
        (legendLabelActivate)="onActivate($event)"
        (legendLabelDeactivate)="onDeactivate($event)">
        <svg:g [attr.transform]="transform" class="bar-chart chart">
          <svg:g ngx-charts-x-axis
            *ngIf="xAxis"
            [xScale]="xScale"
            [dims]="dims"
            [showGridLines]="showGridLines"
            [showLabel]="showXAxisLabel"
            [labelText]="xAxisLabel"
            [ticks]="xAxisTickValues"
            [tickFormatting]="xAxisTickFormatting"
            (dimensionsChanged)="updateXAxisHeight($event)">
          </svg:g>
          <svg:g ngx-charts-y-axis-custom
            *ngIf="yAxis"
            [yScale]="yScale"
            [hilightText]="hilightText"
            [yScale2]="yScale2"
            [dims]="dims"
            [showLabel]="showYAxisLabel"
            [labelText]="yAxisLabel"
            [labelShow]="labelShow"
            [tickFormatting]="yAxisTickFormatting"
            (dimensionsChanged)="updateYAxisWidth($event)">
          </svg:g>
          <svg:g ngx-charts-series-horizontal-custom
            [xScale]="xScale"
            [yScale]="yScale"
            [colors]="colors"
            [series]="results"
            [dims]="dims"
            [gradient]="gradient"
            [showDataLabel]="dataLabel"
            [labelIn]="labelIn"
            [tooltipDisabled]="tooltipDisabled"
            [tooltipTemplate]="tooltipTemplate"
            [activeEntries]="activeEntries"
            [roundEdges]="roundEdges"
            [noBarWhenZero]="noBarWhenZero"
            [animations]="animations"
            (select)="onClick($event)"
            (activate)="onActivate($event)"
            (deactivate)="onDeactivate($event)"
          />
        </svg:g>
      </ngx-charts-chart>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['scss/base-chart.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class BarHorizontalCustomComponent extends BaseChartComponent {

    @Input() legend = false;
    @Input() legendTitle: string = 'Legend';
    @Input() xAxis;
    @Input() yAxis;
    @Input() showXAxisLabel;
    @Input() showYAxisLabel;
    @Input() xAxisLabel;
    @Input() yAxisLabel;
    @Input() tooltipDisabled: boolean = false;
    @Input() labelShow:boolean = false;
    @Input() gradient: boolean;
    @Input() showGridLines: boolean = true;
    @Input() activeEntries: any[] = [];
    @Input() schemeType: string;
    @Input() dataLabel: boolean = false;
    @Input() labelIn: boolean = false;
    @Input() xAxisTickFormatting: any;
    @Input() xAxisTickValues:any[] = [];
    @Input() yAxisTickValues:any;
    @Input() yAxisTickFormatting: any;
    @Input() barPadding = 8;
    @Input() roundDomains: boolean = false;
    @Input() roundEdges: boolean = true;
    @Input() xScaleMax: number;
    @Input() results2: any[] = [];
    @Input() noBarWhenZero: boolean = true;
    @Input() trimXAxisTicks: boolean = true;

    @Output() activate: EventEmitter<any> = new EventEmitter();
    @Output() deactivate: EventEmitter<any> = new EventEmitter();

    @ContentChild('tooltipTemplate') tooltipTemplate: TemplateRef<any>;

    dims: ViewDimensions;
    yScale: any;
    yScale2: any;
    xScale: any;
    hilightText:any;
    xDomain: any;
    yDomain: any;
    yDomain2: any;
    transform: string;
    colors: ColorHelper;
    margin = [10, 20, 10, 20];
    xAxisHeight: number = 0;
    yAxisWidth: number = 0;
    legendOptions: any;

    update(): void {
        
        super.update();
        this.dims = calculateViewDimensions({
            width: this.width,
            height: this.height,
            margins: this.margin,
            showXAxis: this.xAxis,
            showYAxis: this.yAxis,
            xAxisHeight: this.xAxisHeight,
            yAxisWidth: this.yAxisWidth,
            showXLabel: this.showXAxisLabel,
            showYLabel: this.showYAxisLabel,
            showLegend: this.legend,
            legendType: this.schemeType
        });

        this.xScale = this.getXScale();
        this.yScale = this.getYScale();
        this.yScale2 = this.getYScale2();
        
        this.hilightText = this.results2.map(d => d.flag);

        this.setColors();
        this.legendOptions = this.getLegendOptions();

        this.transform = `translate(${this.dims.xOffset - 53} , ${this.margin[0]})`;
    }

    getXScale(): any {
        this.xDomain = this.getXDomain();
        const scale = scaleLinear()
            .range([0, this.dims.width])
            .domain(this.xDomain);

        return this.roundDomains ? scale.nice() : scale;
    }

    getYScale(): any {
        this.yDomain = this.getYDomain();
        this.yDomain2 = this.getYDomain2();
        const spacing = this.yDomain.length / (this.dims.height / this.barPadding + 1);
        
        return scaleBand().rangeRound([0, this.dims.height]).paddingInner(spacing).domain(this.yDomain);
    }

    getYScale2(): any {
        this.yDomain2 = this.getYDomain2();
        const spacing = this.yDomain.length / (this.dims.height / this.barPadding + 1);

        return this.yDomain2;
    }

    getXDomain(): any[] {
        const values = this.results.map(d => d.value);
        const min = Math.min(0, ...values);
        const max = this.xScaleMax
            ? Math.max(this.xScaleMax, ...values)
            : Math.max(...values);
        return [min, max];
    }

    getYDomain(): any[] {
        // console.log('coming',this.results.map(d => ({name:d.name, value:d.value})));
        return this.results.map(d => d.name);
        // return this.results.map(d => ({name:d.name, value:d.value}));
    }

    getYDomain2(): any[] {
        return this.results2.map(d => d);
    }

    onClick(data): void {
        this.select.emit(data);
    }

    setColors(): void {
        let domain;
        if (this.schemeType === 'ordinal') {
            domain = this.yDomain;
        } else {
            domain = this.xDomain;
        }

        this.colors = new ColorHelper(this.scheme, this.schemeType, domain, this.customColors);
    }

    getLegendOptions() {
        const opts = {
            scaleType: this.schemeType,
            colors: undefined,
            domain: [],
            title: undefined
        };
        if (opts.scaleType === 'ordinal') {
            opts.domain = this.yDomain;
            opts.colors = this.colors;
            opts.title = this.legendTitle;
        } else {
            opts.domain = this.xDomain;
            opts.colors = this.colors.scale;
        }

        return opts;
    }

    updateYAxisWidth({ width }): void {
        this.yAxisWidth = width;
        this.update();
    }

    updateXAxisHeight({ height }): void {
        this.xAxisHeight = height;
        this.update();
    }

    onActivate(item) {
        const idx = this.activeEntries.findIndex(d => {
            return d.name === item.name && d.value === item.value && d.series === item.series;
        });
        if (idx > -1) {
            return;
        }

        this.activeEntries = [item, ...this.activeEntries];
        this.activate.emit({ value: item, entries: this.activeEntries });
    }

    onDeactivate(item) {
        const idx = this.activeEntries.findIndex(d => {
            return d.name === item.name && d.value === item.value && d.series === item.series;
        });

        this.activeEntries.splice(idx, 1);
        this.activeEntries = [...this.activeEntries];

        this.deactivate.emit({ value: item, entries: this.activeEntries });
    }

}